import { useNavigate } from 'react-router-dom';
import type { Deck } from '../../types';
import { useAppContext } from '../../context/AppContext';

function timeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function DeckCard({ deck }: { deck: Deck }) {
  const navigate = useNavigate();
  const { savedWords, deleteDeck } = useAppContext();
  const wordCount = savedWords.filter(w => w.deckId === deck.id).length;

  const dueThreshold = Date.now() + 1 * 60 * 60 * 1000;
  const dueCount = savedWords.filter(w => w.deckId === deck.id && (w.dueAt || 0) <= dueThreshold).length;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Delete "${deck.name}" and its ${wordCount} word${wordCount !== 1 ? 's' : ''}? This cannot be undone.`)) {
      deleteDeck(deck.id);
    }
  };

  return (
    <div
      onClick={() => navigate(`/app/decks/${deck.id}`)}
      className="relative bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all group"
    >
      <button
        onClick={handleDelete}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-500 dark:hover:text-red-400 transition-all"
        aria-label="Delete deck"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
      </button>
      <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors pr-8">
        {deck.name}
      </h3>
      <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 mt-3 flex-wrap">
        <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded text-xs font-medium">
          {wordCount} word{wordCount !== 1 ? 's' : ''}
        </span>
        {dueCount > 0 && (
          <span className="bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 px-2 py-0.5 rounded text-xs font-medium">
            {dueCount} due
          </span>
        )}
        <span className="text-xs text-slate-400">
          {deck.lastReviewedAt ? `Studied ${timeAgo(deck.lastReviewedAt)}` : 'Never studied'}
        </span>
      </div>
    </div>
  );
}
